import React, { FC, useEffect, useState } from 'react';
import ModalDialog from '../../../shared/components/modal/ModalDialog';
import { Button, ButtonSize, ButtonTheme } from '../../../shared/common/Button';
import { Article } from '../../../../models/Article';
import { ProductMetaData } from '../../../../models/Product';
import { CopyDetailsPopupDefaultSorting, CopyDetailsPopupLocalContentSorting } from './popupDefaultSorting';
import { IssueCopyDetailsTable } from './IssueCopyDetailsTable';
import { LoanFormat, mapLoanFormatToRequestKey, mapParsedLoanFormat } from '../../../../models/LoanFormat';
import { useTranslation } from 'react-i18next';
import { ProductCopyDetailsTable } from './ProductCopyDetailsTable';
import MetadataUtils from '../../../shared/utils/utils-metadata';
import { AddProduct } from '../../../shared/common/AddProduct/AddProduct';
import './CopyDetailsPopup.scss';
import styled from 'styled-components';
import { CopyDetailsSortBy } from './CopyDetailsSortBy';
import { SingleAddOptions } from '../../../../models/ListContentCheckOptions';

const StDiv = styled.div<{ tableMaxHeight: number }>`
    min-height: ${props => props.tableMaxHeight}px;
`;

type Props = {
    readonly onClosePopup: () => void;
    readonly onAddPrice: (options: SingleAddOptions) => Promise<void>;
    readonly isBuyingOptionEnabled?: boolean;
    readonly product: ProductMetaData & Article;
    readonly isLocalContentVariant?: boolean;
    readonly removeCopyButton?: React.JSX.Element | null;
    readonly shopLanguage: string | undefined;
};

export const CopyDetailsPopup: FC<Props> = ({
    onClosePopup,
    onAddPrice,
    isBuyingOptionEnabled = true,
    product,
    isLocalContentVariant = false,
    removeCopyButton,
    shopLanguage
}) => {
    const [maxHeight, setMaxHeight] = useState(0);
    const availableSortingValues = isLocalContentVariant
        ? CopyDetailsPopupLocalContentSorting
        : CopyDetailsPopupDefaultSorting;
    const [currentSorting, setCurrentSorting] = useState(availableSortingValues[0]);

    const loanFormat = mapParsedLoanFormat(product.loanFormat);
    const { t } = useTranslation();

    useEffect(() => {
        window.addEventListener('resize', handleResize, false);
        handleResize();
        return () => {
            window.removeEventListener('resize', handleResize, false);
        };
    }, []);

    function handleResize() {
        const bodyHeight = document.body.getBoundingClientRect().height;
        const maxHeight = Math.floor(bodyHeight * 0.8 - 275);
        setMaxHeight(maxHeight);
        document.documentElement.style.setProperty('--table-max-height', `${maxHeight}px`);
    }

    const onSelectPrice = async () => {};

    const author = product.authors ? product.authors[0] : null;
    const title = author
        ? t('components.copyDetailsPopup.title', {
              title: product.title,
              firstName: author.firstName,
              lastName: author.lastName
          })
        : `${product.title}`;

    return (
        <>
            <ModalDialog
                show={true}
                titleClassName="black-title"
                title={title}
                onCloseButtonClick={onClosePopup}
                wrapClassName="collection"
            >
                <div className="copy-details-popup">
                    {isBuyingOptionEnabled ? (
                        <div className="add-to-button-line">
                            {MetadataUtils.isProductAvailableForSale(product) ? (
                                <AddProduct
                                    product={product}
                                    onAddPrice={onAddPrice}
                                    onSelectPrice={onSelectPrice}
                                    showSelectedPriceOnly={false}
                                    allowSelection={true}
                                    key={product.id}
                                />
                            ) : (
                                <div key="noLongerForSale" className="available-info">
                                    {t('components.copyDetailsPopup.noLongerForSale')}
                                </div>
                            )}
                        </div>
                    ) : null}
                    {isLocalContentVariant ? removeCopyButton : null}

                    <StDiv tableMaxHeight={maxHeight}>
                        {loanFormat === LoanFormat.ePress ? (
                            <IssueCopyDetailsTable
                                tableMaxHeight={maxHeight}
                                loanFormat={mapLoanFormatToRequestKey(loanFormat)}
                                productId={product.id}
                                currentSorting={currentSorting}
                                sortByComponent={
                                    <CopyDetailsSortBy
                                        currentSorting={currentSorting}
                                        setCurrentSorting={setCurrentSorting}
                                        availableSortingValues={availableSortingValues}
                                    />
                                }
                                shopLanguage={shopLanguage}
                            />
                        ) : (
                            <ProductCopyDetailsTable
                                tableMaxHeight={maxHeight}
                                loanFormatKey={mapLoanFormatToRequestKey(loanFormat)}
                                productId={product.id}
                                currentSorting={currentSorting}
                                isLocalContentVariant={isLocalContentVariant}
                                sortByComponent={
                                    <CopyDetailsSortBy
                                        currentSorting={currentSorting}
                                        setCurrentSorting={setCurrentSorting}
                                        availableSortingValues={availableSortingValues}
                                    />
                                }
                                shopLanguage={shopLanguage}
                            />
                        )}
                    </StDiv>
                    <div className="button-wrapper">
                        <Button
                            onClick={onClosePopup}
                            text={t('close')}
                            theme={ButtonTheme.POSITIVE_INVERT}
                            size={ButtonSize.LARGE}
                        />
                    </div>
                </div>
            </ModalDialog>
        </>
    );
};
